import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { formatCLP, formatDateTime, apiError } from "../../lib/format";
import { toast } from "sonner";
import { HandCoins, CheckCircle, XCircle, Clock, Lightning, LockKey } from "@phosphor-icons/react";

const STATUS_MAP = {
  pendiente: { label: "Pendiente", icon: Clock, color: "text-warn bg-warn/10" },
  aprobado: { label: "Aprobado", icon: CheckCircle, color: "text-success bg-success/10" },
  desembolsado: { label: "Desembolsado", icon: Lightning, color: "text-forest bg-forest/10" },
  rechazado: { label: "Rechazado", icon: XCircle, color: "text-danger bg-danger/10" },
  ofrecido: { label: "Ofrecido", icon: HandCoins, color: "text-burnt bg-burnt/10" },
};

export default function Loans() {
  const { user, refreshMe } = useAuth();
  const [loans, setLoans] = useState([]);
  const [form, setForm] = useState({ amount: "1000000", cuotas: 12, purpose: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/me/loans").then((r) => setLoans(r.data));
  useEffect(() => { load(); refreshMe(); }, []); // eslint-disable-line

  const locked = !user?.loans_unlocked;

  const rate = 1.2;
  const amt = Number(form.amount || 0);
  const monthlyRate = rate / 100;
  const monthly = amt > 0 && form.cuotas > 0 ? (amt * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -form.cuotas)) : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (amt < 500000) return toast.error("Monto mínimo $ 500.000");
    setBusy(true);
    try {
      await api.post("/me/loans", { amount: amt, cuotas: Number(form.cuotas), purpose: form.purpose });
      toast.success("Solicitud enviada. Tu asesor la revisará.");
      setForm({ amount: "1000000", cuotas: 12, purpose: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const accept = async (id) => {
    try {
      await api.post(`/me/loans/${id}/accept`);
      toast.success("Crédito aceptado");
      refreshMe();
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-8">
      <header>
        <div className="label-mono mb-2">Créditos</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">Solicita un crédito a tu medida</h1>
      </header>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Form */}
        {locked ? (
          <div className="lg:col-span-2 bento p-10 flex flex-col items-center text-center justify-center" data-testid="loan-locked">
            <div className="w-14 h-14 flex items-center justify-center bg-warn/10 text-warn mb-4"><LockKey size={28} weight="fill" /></div>
            <h2 className="font-display text-2xl font-bold">Solicitud de créditos bloqueada</h2>
            <p className="text-inkMuted mt-3 max-w-md">
              Para solicitar créditos primero debes activar tu <b>cuenta vista</b> con un primer depósito electrónico.
              Una vez que tu asesor confirme el depósito, habilitará esta opción de forma automática.
            </p>
          </div>
        ) : (
        <form onSubmit={submit} className="lg:col-span-2 bento p-8 space-y-6" data-testid="loan-form">
          <div>
            <label className="label-mono block mb-2">Monto solicitado <span className="normal-case tracking-normal text-inkMuted">· mínimo $ 500.000</span></label>
            <input data-testid="loan-amount" type="number" min="500000" step="50000" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full h-20 border border-hairline bg-surface px-4 font-mono text-4xl" placeholder="1.000.000" />
            <div className="font-mono text-sm text-inkMuted mt-2">{formatCLP(amt)}</div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="label-mono">Cuotas · 3 a 220</label>
              <span className="font-mono text-sm">{form.cuotas} meses</span>
            </div>
            <input data-testid="loan-cuotas" type="range" min="3" max="220" step="1" value={form.cuotas} onChange={(e) => setForm({ ...form, cuotas: Number(e.target.value) })} className="w-full accent-forest" />
            <div className="flex justify-between label-mono mt-1">
              <span>3</span><span>60</span><span>120</span><span>220</span>
            </div>
          </div>

          <div>
            <label className="label-mono block mb-2">Motivo (opcional)</label>
            <input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className="w-full h-12 border border-hairline bg-surface px-4" placeholder="Ej: Remodelación" />
          </div>

          <div className="grid grid-cols-3 border border-hairline bg-bg">
            <Kpi k="Tasa referencial" v={`${rate}% mensual`} />
            <Kpi k="Cuota mensual est." v={formatCLP(Math.round(monthly))} border />
            <Kpi k="Total a pagar" v={formatCLP(Math.round(monthly * form.cuotas))} />
          </div>

          <button data-testid="loan-submit" type="submit" disabled={busy} className="w-full h-12 bg-forest text-bg font-semibold disabled:opacity-60 hover:bg-forest-hover">
            {busy ? "Enviando…" : "Solicitar crédito"}
          </button>
        </form>
        )}

        {/* Sidebar summary */}
        <aside className="bento p-6">
          <div className="label-mono mb-2">Tu historial</div>
          <div className="font-display text-3xl font-black">{loans.length}</div>
          <div className="text-inkMuted text-sm">solicitudes en total</div>
          <div className="mt-6 space-y-3">
            {["pendiente", "aprobado", "desembolsado", "rechazado"].map((s) => {
              const n = loans.filter((l) => l.status === s).length;
              const S = STATUS_MAP[s];
              return (
                <div key={s} className="flex items-center justify-between">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-mono uppercase tracking-wider ${S.color}`}>
                    <S.icon size={12} weight="bold" /> {S.label}
                  </span>
                  <span className="font-mono">{n}</span>
                </div>
              );
            })}
          </div>
        </aside>
      </div>

      <div className="bento" data-testid="loans-list">
        <div className="px-6 py-4 border-b border-hairline"><h2 className="font-display text-xl font-bold">Tus créditos</h2></div>
        {loans.length === 0 ? (
          <div className="p-10 text-center text-inkMuted text-sm">Aún no tienes solicitudes.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="label-mono border-b border-hairline">
                <th className="text-left px-6 py-3">Fecha</th>
                <th className="text-left px-6 py-3">Monto</th>
                <th className="text-left px-6 py-3">Cuotas</th>
                <th className="text-left px-6 py-3">Tasa</th>
                <th className="text-left px-6 py-3">Estado</th>
                <th className="text-left px-6 py-3">Motivo</th>
                <th className="text-right px-6 py-3">Acción</th>
              </tr>
            </thead>
            <tbody>
              {loans.map((l) => {
                const S = STATUS_MAP[l.status] || STATUS_MAP.pendiente;
                return (
                  <tr key={l.id} className="border-b border-hairline">
                    <td className="px-6 py-3 font-mono text-xs">{formatDateTime(l.created_at)}</td>
                    <td className="px-6 py-3 font-mono font-medium">{formatCLP(l.amount)}</td>
                    <td className="px-6 py-3">{l.cuotas}</td>
                    <td className="px-6 py-3 font-mono">{l.rate}%</td>
                    <td className="px-6 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono uppercase tracking-wider ${S.color}`}>
                        <S.icon size={12} weight="bold" /> {S.label}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-inkMuted">{l.purpose || "—"}</td>
                    <td className="px-6 py-3 text-right">
                      {l.status === "ofrecido" && (
                        <button onClick={() => accept(l.id)} className="h-9 px-3 bg-burnt text-bg text-xs font-semibold" data-testid={`accept-${l.id}`}>Aceptar</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Kpi({ k, v, border }) {
  return (
    <div className={`p-4 ${border ? "border-x border-hairline" : ""}`}>
      <div className="label-mono">{k}</div>
      <div className="font-mono text-lg font-medium mt-1">{v}</div>
    </div>
  );
}
